import { SlotKey } from 'repluggable';
import type * as platformEvents from '@wix/platform-editor-sdk/lib/platformEvents.min';
import type {
  CompRef,
  PageRef,
  RouterRef,
  AppData as DmAppData,
  BuilderComponent,
  AppControllerRef,
} from '@wix/document-services-types';
import {
  AppData,
  ComponentRef,
  Rule,
  VariantsData,
  PanelResolveType,
  PageVariant,
} from '@wix/editor-platform-sdk-types';
import type {
  BackwardCompatibleComponentsPlatformService,
  CapabilityResults,
  ComponentCapability,
} from '@wix/editor-platform-manifest-services';
import { BaseInfo } from '@wix/ambassador-app-service-webapp/types';
import type { MarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';

import type { IPlatformInfraPublicAPI } from './platformInfraPublicAPI';
import { IInitConfig } from './sdk';
import * as IInstaller from './installers';
import * as IToolsEntry from './toolsEntry';
import {
  PluginActionContext,
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
  WidgetSlotToMoveContent,
  UniversalSlotPointer,
} from './widgetPlugins';
import {
  IOverrideImplementation,
  IHostType,
  PluginInstallationParams,
  InstallOptions,
  InstallCallbacks,
  PlatformizedComponentOptions,
  HostEditActionsDictionary,
  EditActionDefinition,
  HostEditActionDefinition,
  EditActionBase as SystemAction,
  ExecutionMethodsDictionary,
  Group,
  AppGroup,
  AppExposure,
  ActionNames,
  AppInstallOrigin,
  ActionUiCustomization,
  ActionMetadata,
  AiMetadata,
} from '.';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export type IPublicManifestAPI = ReturnType<
  BackwardCompatibleComponentsPlatformService['getPublicManifestAPI']
>;
export type IPublicHierarchyAPI = ReturnType<
  BackwardCompatibleComponentsPlatformService['getPublicHierarchyAPI']
>;

export interface GetAppMarketListingParams {
  appDefinitionIds: string[];
  languageCode?: string;
  pagingLimit?: number;
}

export {
  FieldAccessibility,
  FontPickerOptions,
  FontPickerOnStyleChange,
  FontStyle,
  OnFontChanged,
} from '@wix/editor-platform-sdk-types';

export interface InitAppOptions {
  origin: {
    type: string;
    info?: object;
  };
}

const slotDefinition = {
  name: 'editorPlatformHostIntegration API',
  layer: 'SITE',
  public: true,
};

export const EditorPlatformHostIntegrationAPI: SlotKey<EditorPlatformHostIntegrationAPI> =
  slotDefinition;

// TODO: This type belongs to manifest-services package, but we need to fix circular dependency
type BindingKey = {
  type: 'dataItem';
  id: string;
  subItemId?: string;
};
export interface EditorPlatformHostIntegrationAPI {
  init: (
    platformApiMethods: any, // TODO fix when we type the host types
    initAppOptions?: InitAppOptions,
    extraNamespaces?: string[],
  ) => void;
  initV2: <K extends IHostType>(config: IInitConfig<K>) => void;
  getWorkerManager: () => Promise<IPlatformInfraPublicAPI>;
  getPlatformInfraAPI: () => Promise<IPlatformInfraPublicAPI>;
  setEditorAPI: (namespace: string, platformAPI: any) => void;
  applications: {
    setAppPublicAPI: (apiName: string) => any;
    createDependenciesDriver: (
      appDefinitionId: string,
      appVersion?: string,
    ) => Promise<IInstaller.DependenciesDataDriver>;
    unifiedComponents: {
      installApps: (
        appDefinitionIds: string[],
        installOptions: InstallOptions,
        callbacks?: InstallCallbacks,
      ) => Promise<void>;
    };
    install: (
      appDefinitionIds: string[],
      installOptions: IInstaller.InstallOptions,
      callbacks: IInstaller.InstallCallbacks,
    ) => void;
    silentInstall: (
      appsToInstallSilently: string[],
      options: IInstaller.SilentInstallAppsOptions,
      serviceConfig: IInstaller.SilentInstallAppsConfig,
      serviceCallbacks: Partial<IInstaller.SilentInstallAppsCallbacks>,
    ) => Promise<void>;
    isIntentInstallRunning: () => boolean;
    notifyIntentInstallDidntHappen: (
      options: IInstaller.IntentInstallOptions,
    ) => void;
    getIsIntentInstallRunningAsync: () => Promise<boolean>;
    isSilentInstallRunning: () => boolean;
    intentInstall: (
      options: IInstaller.IntentInstallOptions,
    ) => Promise<IInstaller.IntentInstallReturnValue>;
    fetchNames: (appDefIds: string[]) => Promise<Record<string, string>>;
    getAppsMarketListing: (
      params: GetAppMarketListingParams,
    ) => Promise<MarketListing[]>;
    isAppUpdateAvailable: (appDefId: string, latestVersion?: string) => boolean;
    addAppToRefresh: (appDefinitionIds: string[]) => Promise<void>;
    refreshVisitedApps: () => Promise<void>;
    openApp: (appDefId: string) => Promise<void>;
  };
  pages: {
    isPageEssential: (pageId: string) => boolean;
    isPageDuplicatable: (pageId: string) => boolean;
  };
  components: {
    getComponentApp: (compRef: CompRef) => {
      appDefinitionId?: string;
      applicationId?: number;
    };
    handleEssentialsComponentsBeforeDelete: (
      compRefs: CompRef[],
    ) => Promise<CompRef[]>;
    getPlatformizedComponentData: (
      compRef: CompRef,
    ) => PlatformizedComponentOptions | null;
    isWidgetStretchable: (
      compRef: CompRef,
      appData?: DmAppData,
    ) => boolean | undefined;
    isWidgetFullWidth: (
      compRef: CompRef,
      appData?: DmAppData,
    ) => boolean | undefined;
    getComponentIdentifiers: (
      compRef: CompRef,
      appData?: DmAppData,
    ) => BaseInfo | undefined;
    isComponentEssential: (compRef: CompRef) => boolean;
    builder: {
      add: (
        builderComponent: BuilderComponent,
        containerRef: CompRef,
      ) => Promise<CompRef>;
      addByType: (
        componentType: string,
        containerPointer: EditorPointer<CompRef>,
        options?: {
          preset?: string;
        },
      ) => Promise<EditorPointer<CompRef> | null>;
    };
    bindings: () => {
      create: <T = unknown>(
        editorPointer: EditorPointer<CompRef>,
        controllerRef: AppControllerRef,
        options: {
          role: string;
          config: T;
          key: BindingKey;
        },
      ) => T;
      delete: (editorPointer: EditorPointer<CompRef>, key: BindingKey) => void;
      update: <T = unknown>(
        editorPointer: EditorPointer<CompRef>,
        key: BindingKey,
        options: {
          config: T;
          role?: string;
        },
      ) => T;
      get: <T = unknown>(
        editorPointer: EditorPointer<CompRef>,
        key: BindingKey,
      ) => T | null;
      isBindable: (editorPointer: EditorPointer<CompRef>) => boolean;
      getBindableControllers: (
        compRef: EditorPointer<CompRef>,
      ) => AppControllerRef[];
      openDataBindingPanel: (dataItemId: string) => void;
    };
    capabilities: () => {
      check: <C extends ComponentCapability>(
        editorPointer: EditorPointer<CompRef>,
        capability: C,
      ) => CapabilityResults[C];
    };
  };
  sdk: {
    overrideImplementation: IOverrideImplementation;
  };
  pageReplace: {
    setReplacerPage: (
      pageRef: PageRef,
      replacerPageRef: PageRef,
      setAsActive: boolean,
    ) => void;
    openPageVariantsPanel: (
      pageTitle: string,
      variants: PageVariant[],
      pageManagingAppDefId: string,
      pageRef: PageRef,
    ) => Promise<void>;
    addVariantToPage: (
      pageRef: PageRef,
      variantPageRef: PageRef,
      rule: Rule,
      variantId: string,
    ) => Promise<void>;
    getPageInfo: (pageRef: PageRef) =>
      | {
          hostAppName: string;
          hostAppDefinitionId: string;
          pageTitle?: string;
          isCustomPage: boolean;
          managingAppDefId?: string | undefined;
        }
      | undefined;
    getPageVariations: (pageRef: PageRef) =>
      | {
          replacers: string[];
          variants: string[];
        }
      | undefined;
    getOriginalPageRef: (pageRef: PageRef) => PageRef | undefined;
    isReplacer: (pageRef: PageRef) => boolean;
    isVariant: (pageRef: PageRef) => boolean;
    isReplacerActive: (pageRef: PageRef) => boolean;
    isReplaceable: (appDefinitionId: string, pageRef: PageRef) => boolean;
    getPageAvailableVariants: (
      appDefinitionId: string,
      pageRef: PageRef,
    ) => VariantsData['variants'] | undefined;
    isRouterExistForPage: (pageRef: PageRef) => boolean;
    createRouter: (pageRef: PageRef) => Promise<RouterRef>;
    connectPageToRouter: (
      routerRef: RouterRef,
      pageRef: PageRef,
    ) => Promise<boolean>;
    createBlankReplacerPage: (pageRef: PageRef) => Promise<void>;
    openDeleteReplacerPageModal: (
      pageRef: PageRef,
    ) => Promise<PanelResolveType>;
    getPremiumFeatureName: (
      appDefinitionId: string,
      pageRef: PageRef,
    ) => string | undefined;
  };
  viewState: {
    getViewState: (
      appData: AppData,
      componentRef: ComponentRef,
    ) => string | undefined;
    setViewState: (
      appData: AppData,
      componentRef: ComponentRef,
      viewState: string,
    ) => void;
  };
  canvasOverlay: {
    hideAll: () => void;
    unhideAll: () => void;
  };
  toolsEntry: {
    register: (listener: IToolsEntry.ToolsEntryEventListener) => void;
    unregister: (listener: IToolsEntry.ToolsEntryEventListener) => void;
  };
  platformEvents: typeof platformEvents;
  slots: {
    autoInstall: (
      pluginInstallationParams: PluginInstallationParams,
      pluginActionContext: PluginActionContext,
    ) => Promise<CompRef | null>;
    autoInstallWithNavigation: (
      pluginInstallationParams: PluginInstallationParams,
      pluginActionContext: PluginActionContext,
    ) => Promise<CompRef | null>;
    /**
     * @deprecated Will be deprecated in upcoming PRs — use
     * `getUniversalSlotPointerByPluginCompRef` instead.
     */
    getSlotPlaceholderRefByContentRef: (
      widgetPluginCompRef: CompRef,
    ) => UniversalSlotPointer | CompRef | undefined;
    getUniversalSlotPointerByPluginCompRef: (
      widgetPluginCompRef: CompRef,
    ) => UniversalSlotPointer | undefined;
    getWidgetHostRefBySlotRef: (compRef: CompRef) => CompRef;
    getWidgetHostRefByWidgetRef: (compRef: CompRef) => CompRef;
    getWidgetSlot: (
      slot: CompRef | UniversalSlotPointer,
      options: { includePrivate: boolean },
    ) => WidgetSlot;
    getWidgetSlots: (
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ) => WidgetSlot[];
    getSlotPosition: (compRef: CompRef) => WidgetSlotPosition | null;
    getCompRefForSlotSelection: (compRef: CompRef) => CompRef | undefined;
    getSlotInBlocksAppEditor: (compRef: CompRef) => WidgetSlot | undefined;
    getWidgetSlotsToMoveContent: (
      compRef: CompRef,
    ) => WidgetSlotToMoveContent[];
    getNearestWidgetHostRef: (compRef: CompRef) => CompRef | undefined;
    hasWidgetSlot: (widgetCompRef: CompRef) => boolean;
    isSlotPublic: (slot: CompRef | UniversalSlotPointer) => boolean;
    moveToSlot: (
      fromSlotComponentRef: CompRef,
      toSlotComponentRef: CompRef,
      pluginActionContext: PluginActionContext,
    ) => Promise<void>;
    highlightSlot: (compRef: CompRef) => Promise<void>;
    unhighlightSlot: (compRef: CompRef) => void;
    isSlotContentPotentialMovable: (
      slot: CompRef | UniversalSlotPointer,
    ) => boolean;
    headerPlugins: {
      isHeaderPlugin: (compRef: CompRef) => boolean;
    };
    plugins: {
      isPluginComponent: (widgetPluginCompRef: CompRef) => boolean;
      showOnPage: (compRef: CompRef) => Promise<void>;
      install: (
        slot: CompRef | UniversalSlotPointer,
        widgetPointer: WidgetPointer,
        pluginActionContext: PluginActionContext,
      ) => Promise<CompRef>;
      uninstall: (
        slot: CompRef | UniversalSlotPointer,
        pluginActionContext: PluginActionContext,
      ) => Promise<void>;
    };
  };
  manifest: IPublicManifestAPI;
  hierarchy: IPublicHierarchyAPI;
  panels: {
    addElements: {
      openIfExist: (compRef: CompRef) => boolean;
    };
    upgradeSiteConfirmation: {
      open: (appDefinitionId: string, premiumFeatureName: string) => void;
    };
    addPanel: {
      getGroups: () => Group[];
      getAppsComponents: () => AppGroup[];
      getEditorExposureAppComponents: () => Promise<AppExposure[]>;
    };
  };

  editActions: {
    getActions: (
      // For backward compatibility, we keep editorPointer (singular)
      // in favor of editorPointers (plural)
      editorPointers: EditorPointer<CompRef> | EditorPointer<CompRef>[],
      metadata?: ActionMetadata,
    ) => {
      nativeActions: HostEditActionDefinition[];
      customActions: EditActionDefinition[];
      systemActions: SystemAction<ActionNames>[];
    };
    contributeHostActions: <TActionName extends string>(
      hostActions: Partial<
        HostEditActionsDictionary<ActionNames | TActionName>
      >,
    ) => void;
    overrideContributedActionUI: (
      actionName: ActionNames,
      uiCustomization: {
        displayName?: string;
        uiCustomization?: ActionUiCustomization;
        aiMetadata?: AiMetadata;
      },
    ) => void;
    contributeExecutionMethods: (
      executionMethods: ExecutionMethodsDictionary,
    ) => void;
  };

  reportBi: (evid: number, event: Record<string, any>) => void;

  addons: {
    install: (
      appDefinitionId: string,
      options: { origin: AppInstallOrigin },
    ) => Promise<void>;
  };
}
